import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { ErrorHandler, Injectable, inject } from '@angular/core';

import { LoggerContext } from '@ph-core/enums/logger-context.enum';
import { reportDatadogRumError } from '@ph-core/services/datadog/datadog';
import { LoggerService } from '@ph-core/services/logger/logger.service';

export interface StructuredApplicationError {
  feature: string;
  message: string;
  stack: string;
}

/**
 * Logs uncaught errors as `{ feature, message, stack }` and forwards them to
 * Datadog RUM when RUM has been initialized.
 * Browser listeners stay registered via `provideBrowserGlobalErrorListeners()`.
 */
@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
  private readonly logger = inject(LoggerService);
  private readonly document = inject(DOCUMENT);
  private readonly baseHref = inject(APP_BASE_HREF, { optional: true }) ?? '/';

  override handleError(error: unknown): void {
    const detail = toStructuredApplicationError(error, this.currentFeature());
    this.logger.error(LoggerContext.GlobalError, 'Unhandled application error', detail);
    reportDatadogRumError(error, detail);
  }

  private currentFeature(): string {
    return readErrorFeature(this.document.location?.pathname ?? '', this.baseHref);
  }
}

/** Route area for the log line: `/home/claims/...` → `claims`, `/home` → `dashboard`. */
export function readErrorFeature(pathname: string, baseHref = '/'): string {
  const path = stripBaseHref(pathname, baseHref);
  const segments = path.split('/').filter(Boolean);

  if (segments[0] === 'home') {
    const feature = segments[1];
    return !feature || feature === 'dashboard' ? 'dashboard' : feature;
  }

  return segments[0] ?? 'app';
}

export function toStructuredApplicationError(error: unknown, feature: string): StructuredApplicationError {
  const described = describeError(error);
  return {
    feature,
    message: described.message,
    stack: described.stack ?? '',
  };
}

function stripBaseHref(pathname: string, baseHref: string): string {
  const base = normalizeBaseHref(baseHref);
  if (base !== '/' && (pathname === base || pathname.startsWith(`${base}/`))) {
    return pathname.slice(base.length) || '/';
  }
  return pathname;
}

function normalizeBaseHref(baseHref: string): string {
  if (!baseHref || baseHref === '/') {
    return '/';
  }

  const withLeadingSlash = baseHref.startsWith('/') ? baseHref : `/${baseHref}`;
  return withLeadingSlash.endsWith('/') ? withLeadingSlash.slice(0, -1) : withLeadingSlash;
}

function describeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return { message: error.message || error.name, stack: error.stack };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  if (isRecord(error)) {
    const message = typeof error['message'] === 'string' ? error['message'] : undefined;
    const stack = typeof error['stack'] === 'string' ? error['stack'] : undefined;
    if (message) {
      return { message, stack };
    }
  }

  if (typeof error === 'number' || typeof error === 'boolean' || typeof error === 'bigint') {
    return { message: String(error) };
  }

  return { message: 'Unknown application error' };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
